import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Users, Plus, KeyRound, Trash2, Copy, Ban, CheckCircle2, Sparkles } from "lucide-react";

function CopyableCredential({ label, value, testid }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copiata`); } catch { toast.error("Copia non riuscita"); }
  };
  return (
    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-mono-plex text-sm text-slate-900 truncate" data-testid={testid}>{value}</div>
      </div>
      <button onClick={copy} className="p-2 text-slate-500 hover:text-slate-900" title="Copia" data-testid={`${testid}-copy`}>
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("tenants");
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [openTenant, setOpenTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState({ name: "", vat: "", type: "company", contact_email: "", location: "", sector: "", initial_user_email: "", initial_user_full_name: "", license_expires_at: "" });
  const [saving, setSaving] = useState(false);
  const [issuedCreds, setIssuedCreds] = useState(null);
  const [openUser, setOpenUser] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", full_name: "", tenant_id: "", role: "company_user" });

  const load = async () => {
    try {
      const [s, t, u] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/tenants"), api.get("/admin/users"),
      ]);
      setStats(s.data); setTenants(t.data); setUsers(u.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  const createTenant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...tenantForm };
      if (!payload.initial_user_email) { delete payload.initial_user_email; delete payload.initial_user_full_name; }
      if (!payload.license_expires_at) payload.license_expires_at = null;
      const { data } = await api.post("/admin/tenants", payload);
      toast.success(`Tenant "${data.tenant.name}" creato`);
      if (data.initial_password) {
        setIssuedCreds({ email: data.initial_user.email, password: data.initial_password });
      }
      setOpenTenant(false);
      setTenantForm({ name: "", vat: "", type: "company", contact_email: "", location: "", sector: "", initial_user_email: "", initial_user_full_name: "", license_expires_at: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore creazione"); }
    finally { setSaving(false); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", userForm);
      setIssuedCreds({ email: data.user.email, password: data.password });
      toast.success("Utente creato");
      setOpenUser(false);
      setUserForm({ email: "", full_name: "", tenant_id: "", role: "company_user" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore creazione utente"); }
    finally { setSaving(false); }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setIssuedCreds({ email: u.email, password: data.password });
      toast.success("Password resettata");
    } catch { toast.error("Errore reset"); }
  };

  const toggleUser = async (u) => {
    try {
      const active = !(u.active !== false);
      await api.patch(`/admin/users/${u.id}?active=${!active ? "true" : "false"}`);
      toast.success(active ? "Utente disattivato" : "Utente riattivato");
      load();
    } catch { toast.error("Errore"); }
  };

  const deleteUser = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const deleteTenant = async (t) => {
    try { await api.delete(`/admin/tenants/${t.id}`); toast.success("Tenant eliminato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const toggleTenantActive = async (t) => {
    try {
      await api.patch(`/admin/tenants/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Licenza disattivata" : "Licenza riattivata");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-2">Area Amministratore</div>
        <h1 className="font-display font-black text-4xl md:text-5xl">Dashboard Admin</h1>
        <p className="text-slate-500 mt-2 text-sm">Gestione tenant, licenze e utenti della piattaforma.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          {[
            ["Tenant totali", stats.tenants_total, "text-slate-900"],
            ["Attivi", stats.tenants_active, "text-emerald-700"],
            ["Aziende", stats.tenants_companies, "text-blue-700"],
            ["Consulenti", stats.tenants_consultants, "text-indigo-700"],
            ["Utenti", stats.users_total, "text-slate-900"],
            ["Audit", stats.audits_total, "text-amber-700"],
          ].map(([label, val, cls]) => (
            <div key={label} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
              <div className={`font-display font-black text-2xl mt-1 ${cls}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
        {[
          { id: "tenants", label: "Tenants & Licenze", icon: Building2 },
          { id: "users", label: "Utenti", icon: Users },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-indigo-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <t.icon className="h-4 w-4 inline mr-1.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "tenants" && (
        <>
          <div className="flex justify-end mb-4">
            <Dialog open={openTenant} onOpenChange={setOpenTenant}>
              <DialogTrigger asChild>
                <Button data-testid="new-tenant-btn" className="h-10 bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4" /> Nuovo Tenant</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-display font-black text-2xl">Nuovo tenant</DialogTitle></DialogHeader>
                <form onSubmit={createTenant} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-600">Tipo</Label>
                      <Select value={tenantForm.type} onValueChange={(v) => setTenantForm({ ...tenantForm, type: v })}>
                        <SelectTrigger data-testid="tenant-type-select" className="mt-2 h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="company">Azienda (usa il portale per il proprio audit)</SelectItem>
                          <SelectItem value="consultant">Consulente (accede a più aziende)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-600">Scadenza licenza</Label>
                      <Input type="date" data-testid="tenant-license-input" value={tenantForm.license_expires_at} onChange={(e) => setTenantForm({ ...tenantForm, license_expires_at: e.target.value })} className="mt-2 h-11" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Ragione sociale *</Label>
                    <Input required data-testid="tenant-name-input" value={tenantForm.name} onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })} className="mt-2 h-11" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-600">P.IVA *</Label>
                      <Input required data-testid="tenant-vat-input" value={tenantForm.vat} onChange={(e) => setTenantForm({ ...tenantForm, vat: e.target.value.toUpperCase() })} className="mt-2 h-11 font-mono-plex" placeholder="IT01234567890" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-600">Città</Label>
                      <Input data-testid="tenant-location-input" value={tenantForm.location} onChange={(e) => setTenantForm({ ...tenantForm, location: e.target.value })} className="mt-2 h-11" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Settore</Label>
                    <Input data-testid="tenant-sector-input" value={tenantForm.sector} onChange={(e) => setTenantForm({ ...tenantForm, sector: e.target.value })} className="mt-2 h-11" />
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <div className="text-xs uppercase tracking-wider text-slate-600 mb-2">Utente iniziale (opzionale)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="email@..." data-testid="tenant-initial-email" type="email" value={tenantForm.initial_user_email} onChange={(e) => setTenantForm({ ...tenantForm, initial_user_email: e.target.value })} className="h-11" />
                      <Input placeholder="Nome Cognome" data-testid="tenant-initial-name" value={tenantForm.initial_user_full_name} onChange={(e) => setTenantForm({ ...tenantForm, initial_user_full_name: e.target.value })} className="h-11" />
                    </div>
                    <div className="mt-1.5 text-[11px] text-slate-500">Se compili l'email, verrà creato subito il primo utente e ricevi la password una tantum.</div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving} data-testid="tenant-save-btn" className="bg-indigo-600 hover:bg-indigo-700">{saving ? "Creazione…" : "Crea Tenant"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {tenants.map((t) => (
              <div key={t.id} data-testid={`tenant-card-${t.id}`} className="bg-white border border-slate-200 rounded-lg p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${t.type === "company" ? "bg-blue-50 text-blue-700" : "bg-indigo-50 text-indigo-700"}`}>{t.type === "company" ? "Azienda" : "Consulente"}</span>
                      {!t.active && <span className="text-[10px] uppercase tracking-wider bg-red-50 text-red-700 px-1.5 py-0.5 rounded">Disattivato</span>}
                    </div>
                    <div className="font-display font-bold text-lg text-slate-900 truncate">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-1 font-mono-plex">{t.vat}</div>
                    {t.contact_email && <div className="text-xs text-slate-500 mt-1 truncate">{t.contact_email}</div>}
                    <div className="text-[11px] text-slate-500 mt-2 font-mono-plex">{t.users_count} utenti · scad. {t.license_expires_at || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleTenantActive(t)} title={t.active ? "Disattiva" : "Riattiva"} data-testid={`tenant-toggle-${t.id}`} className="p-2 text-slate-500 hover:text-amber-700 hover:bg-amber-50 rounded-md">
                      {t.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`tenant-delete-${t.id}`} className="p-2 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 className="h-4 w-4" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare tenant "{t.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>Tutti gli utenti, aziende, audit e template collegati verranno rimossi. Operazione irreversibile.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteTenant(t)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "users" && (
        <>
          <div className="flex justify-end mb-4">
            <Dialog open={openUser} onOpenChange={setOpenUser}>
              <DialogTrigger asChild>
                <Button data-testid="new-user-btn" className="h-10 bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4" /> Nuovo Utente</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display font-black text-2xl">Nuovo utente</DialogTitle></DialogHeader>
                <form onSubmit={createUser} className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Tenant *</Label>
                    <Select value={userForm.tenant_id} onValueChange={(v) => setUserForm({ ...userForm, tenant_id: v, role: (tenants.find((x) => x.id === v)?.type === "consultant" ? "consultant" : "company_user") })}>
                      <SelectTrigger data-testid="user-tenant-select" className="mt-2 h-11"><SelectValue placeholder="Seleziona tenant…" /></SelectTrigger>
                      <SelectContent>
                        {tenants.map((t) => (<SelectItem key={t.id} value={t.id}>[{t.type === "company" ? "Az" : "Cons"}] {t.name} · {t.vat}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Email *</Label>
                    <Input required data-testid="user-email-input" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Nome completo *</Label>
                    <Input required data-testid="user-name-input" value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} className="mt-2 h-11" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving} data-testid="user-save-btn" className="bg-indigo-600 hover:bg-indigo-700">Crea Utente</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Ruolo</th>
                  <th className="text-left px-4 py-3">Tenant</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-mono-plex text-xs">{u.email}</td>
                    <td className="px-4 py-2.5">{u.full_name}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${u.role === "admin" ? "bg-red-50 text-red-700" : u.role === "consultant" ? "bg-indigo-50 text-indigo-700" : "bg-blue-50 text-blue-700"}`}>{u.role}</span>
                      {u.active === false && <span className="ml-2 text-[10px] uppercase text-slate-500">disattivato</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{u.tenant_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      {u.role !== "admin" && (
                        <>
                          <button onClick={() => resetPassword(u)} title="Reset password" data-testid={`user-reset-${u.id}`} className="p-1.5 text-slate-500 hover:text-indigo-700"><KeyRound className="h-4 w-4" /></button>
                          <button onClick={() => toggleUser(u)} title={u.active !== false ? "Disattiva" : "Attiva"} data-testid={`user-toggle-${u.id}`} className="p-1.5 text-slate-500 hover:text-amber-700">{u.active !== false ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}</button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`user-delete-${u.id}`} className="p-1.5 text-slate-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Eliminare {u.email}?</AlertDialogTitle></AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteUser(u)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Issued credentials modal */}
      <Dialog open={!!issuedCreds} onOpenChange={(v) => !v && setIssuedCreds(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" /> Credenziali generate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 leading-relaxed">Comunica queste credenziali all'utente. La password verrà mostrata <strong>una sola volta</strong>.</p>
            {issuedCreds && (
              <>
                <CopyableCredential label="Email" value={issuedCreds.email} testid="creds-email" />
                <CopyableCredential label="Password" value={issuedCreds.password} testid="creds-password" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setIssuedCreds(null)} data-testid="creds-close-btn" className="bg-slate-900 hover:bg-slate-800">Ho copiato — Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
